/**
 * @typedef {Object} InventoryItem
 * @property {string} id - The unique identifier of the item
 * @property {string} name - The name of the item
 * @property {string} description - The description of the item
 * @property {string} status - The current status of the item
 * @property {string} [reCommerceFlow] - Optional reCommerce flow status
 */

/**
 * @typedef {Object} InventoryFilter
 * @property {string} [search] - Optional search term
 * @property {string} [status] - Optional status filter
 * @property {string} [sortBy] - Optional sort field
 * @property {string} [sortOrder] - Optional sort order
 */

/**
 * @typedef {Object} InventoryMetrics
 * @property {number} total - Total number of items
 * @property {Object.<string, number>} byStatus - Count of items by status
 */

/**
 * @typedef {Object} Location
 * @property {string} id - The unique identifier of the location
 * @property {string} name - The name of the location
 * @property {string} type - The type of location
 * @property {string} address - The address of the location
 */

/**
 * @typedef {Object} Transfer
 * @property {string} id - The unique identifier of the transfer
 * @property {string} sourceLocationId - The source location ID
 * @property {string} destinationLocationId - The destination location ID
 * @property {string[]} itemIds - Array of item IDs being transferred
 * @property {string} status - The status of the transfer
 * @property {Date} createdAt - When the transfer was created
 * @property {Date} updatedAt - When the transfer was last updated
 */

/** @typedef {'available'|'in_use'|'maintenance'|'retired'} InventoryStatus */

const API_BASE_URL = '/api/inventory';

/**
 * Fetch inventory items with optional filters
 * @param {InventoryFilter} [filters] - Optional filters to apply
 * @returns {Promise<InventoryItem[]>}
 */
export async function fetchInventoryItems(filters) {
  const queryParams = new URLSearchParams();

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        if (value instanceof Date) {
          queryParams.append(key, value.toISOString());
        } else {
          queryParams.append(key, value.toString());
        }
      }
    });
  }

  const response = await fetch(`${API_BASE_URL}/items?${queryParams.toString()}`);
  if (!response.ok) {
    throw new Error('Failed to fetch inventory items');
  }
  return response.json();
}

/**
 * Fetch a single inventory item by ID
 * @param {string} id - The ID of the item to fetch
 * @returns {Promise<InventoryItem>}
 */
export async function fetchInventoryItem(id) {
  const response = await fetch(`${API_BASE_URL}/items/${id}`);
  if (!response.ok) {
    throw new Error('Failed to fetch inventory item');
  }
  return response.json();
}

/**
 * Update an inventory item
 * @param {string} id - The ID of the item to update
 * @param {Partial<InventoryItem>} data - The data to update
 * @returns {Promise<InventoryItem>}
 */
export async function updateInventoryItem(id, data) {
  const response = await fetch(`${API_BASE_URL}/items/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error('Failed to update inventory item');
  }
  return response.json();
}

/**
 * Update an inventory item's status
 * @param {string} id - The ID of the item to update
 * @param {InventoryStatus} status - The new status
 * @param {'resale'|'repair'|'rental'} [reCommerceFlow] - Optional reCommerce flow
 * @returns {Promise<InventoryItem>}
 */
export async function updateInventoryStatus(id, status, reCommerceFlow) {
  const response = await fetch(`${API_BASE_URL}/items/${id}/status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ status, reCommerceFlow }),
  });
  if (!response.ok) {
    throw new Error('Failed to update inventory status');
  }
  return response.json();
}

/**
 * Fetch all locations
 * @returns {Promise<Location[]>}
 */
export async function fetchLocations() {
  console.log("Fetching inventory locations...");
  
  // Check if we're in development/mock mode and use mock data directly
  if (process.env.NODE_ENV === 'development' || import.meta.env.VITE_USE_MOCK_DATA === 'true') {
    console.log("Using hardcoded mock locations data");
    
    // Mock inventory locations data
    const mockLocations = [
      {
        id: 'loc-001',
        code: 'WHR-001',
        name: 'Main Warehouse',
        type: 'warehouse',
        address: '123 Distribution Blvd, Portland, OR 97201',
        description: 'Main distribution warehouse for the Pacific Northwest region'
      },
      {
        id: 'loc-002',
        code: 'STR-105',
        name: 'Downtown Store',
        type: 'store',
        address: '456 Main Street, Portland, OR 97204',
        description: 'Flagship retail location in downtown Portland'
      },
      {
        id: 'loc-003',
        code: 'CDC-023',
        name: 'Central Distribution',
        type: 'distribution_center',
        address: '789 Logistics Way, Beaverton, OR 97005',
        description: 'Central distribution hub for all Pacific region inventory'
      },
      {
        id: 'loc-004',
        code: 'WHR-002',
        name: 'East Coast Warehouse',
        type: 'warehouse',
        address: '101 Storage Lane, Newark, NJ 07102',
        description: 'Primary warehouse serving the northeastern US'
      },
      {
        id: 'loc-005',
        code: 'STR-217',
        name: 'Seattle Store',
        type: 'store',
        address: '202 Pike Street, Seattle, WA 98101',
        description: 'Retail location in downtown Seattle'
      },
      {
        id: 'loc-006',
        code: 'WHS-010',
        name: 'South Regional Storage',
        type: 'warehouse',
        address: '303 Industrial Park, Atlanta, GA 30301',
        description: 'Regional warehouse for the southeastern states'
      },
      {
        id: 'loc-007',
        code: 'RDC-005',
        name: 'Midwest Distribution',
        type: 'distribution_center',
        address: '404 Shipping Lane, Chicago, IL 60601',
        description: 'Regional distribution center for midwest operations'
      }
    ];
    
    return Promise.resolve(mockLocations);
  }
  
  // Normal API call for production
  const response = await fetch(`${API_BASE_URL}/locations`);
  if (!response.ok) {
    throw new Error('Failed to fetch locations');
  }
  return response.json();
}

/**
 * Fetch a single location by ID
 * @param {string} id - The ID of the location to fetch
 * @returns {Promise<Location>}
 */
export async function fetchLocation(id) {
  console.log("Fetching inventory location by ID:", id);
  
  // Check if we're in development/mock mode and use mock data directly
  if (process.env.NODE_ENV === 'development' || import.meta.env.VITE_USE_MOCK_DATA === 'true') {
    console.log("Using hardcoded mock location data");
    
    // Use the fetchLocations function to get all mock locations
    const allLocations = await fetchLocations();
    const location = allLocations.find(loc => loc.id === id);
    
    if (!location) {
      throw new Error(`Location with ID ${id} not found`);
    }
    
    return Promise.resolve(location);
  }
  
  // Normal API call for production
  const response = await fetch(`${API_BASE_URL}/locations/${id}`);
  if (!response.ok) {
    throw new Error('Failed to fetch location');
  }
  return response.json();
}

/**
 * Create a new location
 * @param {Omit<Location, 'id'>} location - The location data to create
 * @returns {Promise<Location>}
 */
export async function createLocation(location) {
  console.log("Creating new inventory location:", location);
  
  // Check if we're in development/mock mode and use mock data directly
  if (process.env.NODE_ENV === 'development' || import.meta.env.VITE_USE_MOCK_DATA === 'true') {
    console.log("Using mock implementation for location creation");
    
    // Generate a unique ID
    const newLocation = {
      id: `loc-${Date.now().toString().slice(-6)}`,
      ...location
    };
    
    return Promise.resolve(newLocation);
  }
  
  // Normal API call for production
  const response = await fetch(`${API_BASE_URL}/locations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(location),
  });
  if (!response.ok) {
    throw new Error('Failed to create location');
  }
  return response.json();
}

/**
 * Update a location
 * @param {string} id - The ID of the location to update
 * @param {Partial<Location>} data - The data to update
 * @returns {Promise<Location>}
 */
export async function updateLocation(id, data) {
  console.log("Updating inventory location:", id, data);
  
  // Check if we're in development/mock mode and use mock data directly
  if (process.env.NODE_ENV === 'development' || import.meta.env.VITE_USE_MOCK_DATA === 'true') {
    console.log("Using mock implementation for location update");
    
    // Get the location first
    try {
      const location = await fetchLocation(id);
      
      // Update with new data
      const updatedLocation = {
        ...location,
        ...data
      };
      
      return Promise.resolve(updatedLocation);
    } catch (error) {
      throw new Error(`Failed to update location: ${error.message}`);
    }
  }
  
  // Normal API call for production
  const response = await fetch(`${API_BASE_URL}/locations/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error('Failed to update location');
  }
  return response.json();
}

/**
 * Fetch all transfers
 * @returns {Promise<Transfer[]>}
 */
export async function fetchTransfers() {
  console.log("Fetching inventory transfers...");
  
  // Check if we're in development/mock mode and use mock data directly
  if (process.env.NODE_ENV === 'development' || import.meta.env.VITE_USE_MOCK_DATA === 'true') {
    console.log("Using hardcoded mock transfers data");
    
    // Fetch locations to reference in transfers
    const locations = await fetchLocations();
    
    // Mock transfers data
    const mockTransfers = [
      {
        id: 'transfer-001',
        reference: 'T-2024-0001',
        sourceLocationId: locations[0].id,
        sourceLocationName: locations[0].name,
        destinationLocationId: locations[1].id,
        destinationLocationName: locations[1].name,
        status: 'pending',
        itemCount: 12,
        createdAt: '2024-05-10T08:15:30Z',
        updatedAt: '2024-05-10T08:15:30Z',
        items: ['item-001', 'item-002', 'item-003', 'item-004', 'item-005']
      },
      {
        id: 'transfer-002',
        reference: 'T-2024-0002',
        sourceLocationId: locations[2].id,
        sourceLocationName: locations[2].name,
        destinationLocationId: locations[3].id,
        destinationLocationName: locations[3].name,
        status: 'in_transit',
        itemCount: 8,
        createdAt: '2024-05-08T14:22:45Z',
        updatedAt: '2024-05-09T09:30:15Z',
        items: ['item-011', 'item-012', 'item-013']
      },
      {
        id: 'transfer-003',
        reference: 'T-2024-0003',
        sourceLocationId: locations[1].id,
        sourceLocationName: locations[1].name,
        destinationLocationId: locations[4].id,
        destinationLocationName: locations[4].name,
        status: 'completed',
        itemCount: 15,
        createdAt: '2024-05-05T10:45:00Z',
        updatedAt: '2024-05-06T16:20:30Z',
        items: ['item-021', 'item-022', 'item-023', 'item-024']
      },
      {
        id: 'transfer-004',
        reference: 'T-2024-0004',
        sourceLocationId: locations[3].id,
        sourceLocationName: locations[3].name,
        destinationLocationId: locations[0].id,
        destinationLocationName: locations[0].name,
        status: 'cancelled',
        itemCount: 5,
        createdAt: '2024-05-07T09:10:15Z',
        updatedAt: '2024-05-07T11:30:45Z',
        items: ['item-031', 'item-032']
      },
      {
        id: 'transfer-005',
        reference: 'T-2024-0005',
        sourceLocationId: locations[0].id,
        sourceLocationName: locations[0].name,
        destinationLocationId: locations[2].id,
        destinationLocationName: locations[2].name,
        status: 'pending',
        itemCount: 20,
        createdAt: '2024-05-11T13:45:00Z',
        updatedAt: '2024-05-11T13:45:00Z',
        items: ['item-041', 'item-042', 'item-043', 'item-044', 'item-045']
      }
    ];
    
    return Promise.resolve(mockTransfers);
  }
  
  // Normal API call for production
  const response = await fetch(`${API_BASE_URL}/transfers`);
  if (!response.ok) {
    throw new Error('Failed to fetch transfers');
  }
  return response.json();
}

/**
 * Fetch a single transfer by ID
 * @param {string} id - The ID of the transfer to fetch
 * @returns {Promise<Transfer>}
 */
export async function fetchTransfer(id) {
  console.log("Fetching inventory transfer by ID:", id);
  
  // Check if we're in development/mock mode and use mock data directly
  if (process.env.NODE_ENV === 'development' || import.meta.env.VITE_USE_MOCK_DATA === 'true') {
    console.log("Using hardcoded mock transfer data");
    
    // Use the fetchTransfers function to get all mock transfers
    const allTransfers = await fetchTransfers();
    const transfer = allTransfers.find(t => t.id === id);
    
    if (!transfer) {
      throw new Error(`Transfer with ID ${id} not found`);
    }
    
    return Promise.resolve(transfer);
  }
  
  // Normal API call for production
  const response = await fetch(`${API_BASE_URL}/transfers/${id}`);
  if (!response.ok) {
    throw new Error('Failed to fetch transfer');
  }
  return response.json();
}

/**
 * Create a new transfer
 * @param {Omit<Transfer, 'id'|'status'|'createdAt'|'updatedAt'>} transfer - The transfer data to create
 * @returns {Promise<Transfer>}
 */
export async function createTransfer(transfer) {
  console.log("Creating new inventory transfer:", transfer);
  
  // Check if we're in development/mock mode and use mock data directly
  if (process.env.NODE_ENV === 'development' || import.meta.env.VITE_USE_MOCK_DATA === 'true') {
    console.log("Using mock implementation for transfer creation");
    
    // Get the source and destination locations
    let sourceLocationName = "Unknown Source";
    let destinationLocationName = "Unknown Destination";
    
    try {
      const locations = await fetchLocations();
      const sourceLocation = locations.find(loc => loc.id === transfer.sourceLocationId);
      const destinationLocation = locations.find(loc => loc.id === transfer.destinationLocationId);
      
      if (sourceLocation) {
        sourceLocationName = sourceLocation.name;
      }
      
      if (destinationLocation) {
        destinationLocationName = destinationLocation.name;
      }
    } catch (error) {
      console.error("Error fetching locations for transfer creation:", error);
    }
    
    // Generate a unique ID and reference
    const newTransfer = {
      id: `transfer-${Date.now().toString().slice(-6)}`,
      reference: `T-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`,
      status: 'pending',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      sourceLocationName,
      destinationLocationName,
      itemCount: transfer.items?.length || 0,
      ...transfer
    };
    
    return Promise.resolve(newTransfer);
  }
  
  // Normal API call for production
  const response = await fetch(`${API_BASE_URL}/transfers`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(transfer),
  });
  if (!response.ok) {
    throw new Error('Failed to create transfer');
  }
  return response.json();
}

/**
 * Update a transfer's status
 * @param {string} id - The ID of the transfer to update
 * @param {Transfer['status']} status - The new status
 * @returns {Promise<Transfer>}
 */
export async function updateTransferStatus(id, status) {
  console.log("Updating inventory transfer status:", id, status);
  
  // Check if we're in development/mock mode and use mock data directly
  if (process.env.NODE_ENV === 'development' || import.meta.env.VITE_USE_MOCK_DATA === 'true') {
    console.log("Using mock implementation for transfer status update");
    
    // Get the transfer first
    try {
      const transfer = await fetchTransfer(id);
      
      // Update with new status
      const updatedTransfer = {
        ...transfer,
        status,
        updatedAt: new Date().toISOString()
      };
      
      return Promise.resolve(updatedTransfer);
    } catch (error) {
      throw new Error(`Failed to update transfer status: ${error.message}`);
    }
  }
  
  // Normal API call for production
  const response = await fetch(`${API_BASE_URL}/transfers/${id}/status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ status }),
  });
  if (!response.ok) {
    throw new Error('Failed to update transfer status');
  }
  return response.json();
}

/**
 * Fetch inventory metrics
 * @returns {Promise<InventoryMetrics>}
 */
export async function fetchInventoryMetrics() {
  const response = await fetch(`${API_BASE_URL}/metrics`);
  if (!response.ok) {
    throw new Error('Failed to fetch inventory metrics');
  }
  return response.json();
}

/**
 * @typedef {Object} BulkUpdateStatusRequest
 * @property {string[]} itemIds - Array of item IDs to update
 * @property {InventoryStatus} status - The new status to set
 * @property {'resale'|'repair'|'rental'} [reCommerceFlow] - Optional reCommerce flow
 */

/**
 * Bulk update status for multiple items
 * @param {BulkUpdateStatusRequest} data - The bulk update data
 * @returns {Promise<void>}
 */
export async function bulkUpdateStatus(data) {
  const response = await fetch(`${API_BASE_URL}/items/bulk/status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error('Failed to update items status');
  }
}

/**
 * Import inventory from a file
 * @param {File} file - The file to import
 * @returns {Promise<void>}
 */
export async function importInventory(file) {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/import`, {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) {
    throw new Error('Failed to import inventory');
  }
}

/**
 * Export inventory to a CSV file
 * @returns {Promise<void>}
 */
export async function exportInventory() {
  const response = await fetch(`${API_BASE_URL}/export`);
  if (!response.ok) {
    throw new Error('Failed to export inventory');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'inventory.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

/**
 * Fetch transfers for a specific item
 * @param {string} itemId - The ID of the item to fetch transfers for
 * @returns {Promise<Transfer[]>}
 */
export async function fetchItemTransfers(itemId) {
  console.log("Fetching transfers for item:", itemId);
  
  // Check if we're in development/mock mode and use mock data directly
  if (process.env.NODE_ENV === 'development' || import.meta.env.VITE_USE_MOCK_DATA === 'true') {
    console.log("Using mock implementation for item transfers");
    
    // Get all transfers
    const allTransfers = await fetchTransfers();
    
    // Filter transfers that include this item
    const itemTransfers = allTransfers.filter(transfer => 
      transfer.items && transfer.items.includes(itemId)
    );
    
    return Promise.resolve(itemTransfers);
  }
  
  // Normal API call for production
  const response = await fetch(`${API_BASE_URL}/items/${itemId}/transfers`);
  if (!response.ok) {
    throw new Error('Failed to fetch item transfers');
  }
  return response.json();
} 